"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";

const USE_CASES = [
  "public speaking",
  "presentations",
  "job interviews",
  "startup pitches",
  "self-introductions",
];

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.ali.pitch_buddy";

export default function HeroSection() {
  const [useCaseIndex, setUseCaseIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setUseCaseIndex((prev) => (prev + 1) % USE_CASES.length);
    }, 2600);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <section
      aria-labelledby="hero-title"
      className="relative pt-10 pb-14 md:pt-14 md:pb-18 lg:pt-16 lg:pb-20"
    >
      <div className="hero-wide-shell">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)] lg:gap-x-12 lg:gap-y-0 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-x-12 2xl:gap-x-11 min-[1920px]:gap-x-14">
          <div className="relative z-10 max-w-2xl lg:mx-0 lg:min-w-0 lg:max-w-none lg:pr-4 xl:pr-6 2xl:max-w-[36rem] 2xl:pr-5 min-[1920px]:max-w-[38rem] min-[1920px]:pr-6">
            <h1
              id="hero-title"
              className="flex min-h-[3.35em] flex-col text-[clamp(2.125rem,3.75vw+1.05rem,4.6rem)] font-extrabold leading-[1.1] tracking-[-0.035em] text-foreground md:min-h-[3.28em]"
            >
              <span>Practice</span>
              <span
                className="relative inline-flex min-h-[1.08em] max-w-full md:w-[22ch] md:whitespace-nowrap lg:w-auto lg:whitespace-nowrap 2xl:pr-1"
                aria-live="polite"
                aria-atomic="true"
              >
                <span
                  key={useCaseIndex}
                  className="hero-rotating-word text-amber-600 dark:text-amber-500"
                >
                  {USE_CASES[useCaseIndex]}.
                </span>
              </span>
              <span>Get real feedback.</span>
            </h1>

            <p className="mt-6 max-w-xl text-base font-normal leading-relaxed text-neutral-700 lg:text-lg dark:text-neutral-300">
              PitchBuddy helps you practice real speaking situations with guided
              drills, instant feedback, and progress tracking.
            </p>

            <div id="download-app" className="mt-8 flex flex-wrap items-center gap-3 sm:mt-10">
              <Link
                href="https://apps.apple.com/us/app/pitchbuddy-public-speaking/id6752858863"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download PitchBuddy on the App Store"
                className="inline-flex h-[52px] items-center justify-center rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:drop-shadow-[0_10px_22px_rgba(30,26,36,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <Image
                  src="/download-on-app-store.svg"
                  alt="Download on the App Store"
                  width={135}
                  height={41}
                  priority
                  className="h-[52px] w-auto rounded-xl"
                />
              </Link>

              <Link
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get PitchBuddy on Google Play"
                className="inline-flex h-[52px] items-center justify-center rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:drop-shadow-[0_10px_22px_rgba(30,26,36,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <Image
                  src="/download-on-google-store.svg"
                  alt="Get it on Google Play"
                  width={135}
                  height={41}
                  priority
                  className="h-[52px] w-auto rounded-xl"
                />
              </Link>
            </div>
          </div>

          <div className="relative z-0 flex min-h-0 justify-center overflow-visible lg:-mt-3 lg:justify-end 2xl:justify-center xl:-mt-1">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-5 bottom-4 top-7 rounded-full bg-primary/22 blur-3xl sm:inset-x-7 md:bottom-3 md:top-6 lg:inset-x-2 lg:bottom-2 lg:top-4 xl:inset-x-0"
            />
            <div className="relative w-full max-w-xl origin-left sm:max-w-2xl md:max-w-3xl lg:max-w-[54rem] lg:translate-x-[calc(0.5rem+24px)] lg:translate-y-1 lg:scale-[1.05] xl:max-w-[56rem] xl:translate-x-[calc(0.75rem+32px)] xl:scale-[1.07] 2xl:max-w-[64rem] 2xl:translate-x-0 2xl:scale-[1.09] min-[1920px]:max-w-[70rem] min-[1920px]:-translate-x-1 min-[1920px]:scale-[1.1]">
              <Image
                src="/hero.png"
                alt="PitchBuddy mobile app screens showing guided speaking drills, feedback, and progress insights"
                width={1200}
                height={900}
                sizes="(max-width: 640px) 100vw, (max-width: 1023px) 92vw, (max-width: 1535px) 58vw, (max-width: 1919px) 56vw, 960px"
                className="relative z-10 h-auto w-full object-contain drop-shadow-[0_22px_40px_rgba(23,23,23,0.17),0_8px_16px_rgba(23,23,23,0.08)]"
                priority
                fetchPriority="high"
                quality={65}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
